import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Loader2,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { QUESTIONS, COUNTRY_CODES } from "../data/blueprint";
import { SITE } from "../data/content";
import useReveal from "../hooks/useReveal";

export const COLOR_HEX = {
    red: "#B15A5A",
    orange: "#C48A5A",
    amber: "#C5A880",
    "yellow-green": "#A9B08F",
    green: "#8A9A86",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useSetTitle(title) {
    useEffect(() => {
        const prev = document.title;
        document.title = title;
        return () => {
            document.title = prev;
        };
    }, [title]);
}
export { useSetTitle };

/* ============================================================ */
/* Intro                                                        */
/* ============================================================ */
function Intro({ onStart }) {
    useReveal();
    return (
        <section
            className="pt-24 pb-16 lg:pb-24"
            data-testid="blueprint-intro"
        >
            <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10">
                <p className="ssh-eyebrow">Free · Self-Reflection Assessment</p>
                <h1 className="mt-6 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                    Your Relationship{" "}
                    <em className="italic text-[#A68A6B]">Blueprint.</em>
                </h1>
                <p className="mt-6 text-lg leading-[1.7] text-[#4A4947]">
                    Discover where you currently are in your relationship — and
                    what may be keeping you stuck.
                </p>
                <div className="mt-10 space-y-5 text-base lg:text-lg leading-[1.75] text-[#2C2B29] font-light">
                    <p>
                        Relationships can feel confusing, especially when part
                        of you understands what is happening but another part
                        struggles to create change.
                    </p>
                    <p>
                        This 20-question self-reflection assessment will help
                        you explore four areas:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 pl-1">
                        {[
                            "Connection",
                            "Emotional impact",
                            "Stuckness",
                            "Readiness for change",
                        ].map((x) => (
                            <li key={x} className="flex gap-3">
                                <span className="text-[#A68A6B]">—</span>
                                <span>{x}</span>
                            </li>
                        ))}
                    </ul>
                    <p>
                        At the end, you&apos;ll see a personalised{" "}
                        <em>Relationship Blueprint</em> with your current stage
                        and four category scores.
                    </p>
                </div>
                <div className="mt-10">
                    <Button
                        onClick={onStart}
                        className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3"
                        data-testid="bp-start"
                    >
                        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                        Discover My Relationship Blueprint
                    </Button>
                </div>
                <p className="mt-10 text-xs text-[#75736F] leading-relaxed max-w-2xl border-l-2 border-[#EFEBE4] pl-4">
                    This is a self-reflection tool for personal insight and is
                    not a psychological diagnosis, clinical assessment,
                    relationship-safety assessment, or substitute for
                    professional mental-health support.
                </p>
            </div>
        </section>
    );
}

/* ============================================================ */
/* Question Card                                                */
/* ============================================================ */
export function QuestionCard({ question, value, onChange, index, total }) {
    const isMulti = question.multi === true;
    const progressPct = ((index) / total) * 100;

    const toggleMulti = (opt) => {
        const current = Array.isArray(value) ? value : [];
        const next = current.includes(opt)
            ? current.filter((v) => v !== opt)
            : [...current, opt];
        onChange(next);
    };

    return (
        <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-[#75736F]">
                <span data-testid="bp-progress-label">
                    Question {index + 1} of {total}
                </span>
                <span className="hidden sm:inline">
                    {question.category === "disconnection" && "Connection"}
                    {question.category === "distress" && "Emotional impact"}
                    {question.category === "stuckness" && "Stuckness"}
                    {question.category === "readiness" && "Readiness"}
                    {question.category === "personal" && "Personal"}
                </span>
            </div>
            <div className="mt-3 h-[2px] w-full bg-[#EFEBE4] overflow-hidden">
                <motion.div
                    className="h-full bg-[#A68A6B]"
                    initial={false}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                    <h2 className="mt-10 font-serif font-light tracking-tight leading-[1.15] text-2xl sm:text-3xl lg:text-4xl text-[#2C2B29]">
                        {question.text}
                    </h2>
                    {question.subtext && (
                        <p className="mt-3 text-sm italic text-[#75736F]">
                            {question.subtext}
                        </p>
                    )}

                    <div className="mt-10 flex flex-col gap-3">
                        {question.options.map((opt, i) => {
                            const label =
                                typeof opt === "string" ? opt : opt.text;
                            const optValue =
                                typeof opt === "string" ? opt : opt.score;
                            const active = isMulti
                                ? Array.isArray(value) &&
                                  value.includes(label)
                                : value === optValue ||
                                  (optValue === null && value === "na");

                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                        if (isMulti) {
                                            toggleMulti(label);
                                        } else if (typeof opt === "string") {
                                            onChange(label);
                                        } else if (opt.score === null) {
                                            onChange("na");
                                        } else {
                                            onChange(opt.score);
                                        }
                                    }}
                                    className={`text-left w-full border transition-colors px-5 py-4 sm:px-7 sm:py-5 flex items-start gap-4 ${
                                        active
                                            ? "border-[#A68A6B] bg-[#F6F0E5]"
                                            : "border-[#EFEBE4] bg-white hover:border-[#A68A6B] hover:bg-[#FAF8F5]"
                                    }`}
                                    data-testid={`bp-q${question.id}-opt-${i}`}
                                >
                                    <span
                                        className={`mt-1 h-4 w-4 flex-shrink-0 border ${
                                            isMulti ? "" : "rounded-full"
                                        } flex items-center justify-center ${
                                            active
                                                ? "border-[#A68A6B] bg-[#A68A6B]"
                                                : "border-[#A68A6B]/60"
                                        }`}
                                    >
                                        {active && (
                                            <CheckCircle2
                                                className="h-3 w-3 text-white"
                                                strokeWidth={2.5}
                                            />
                                        )}
                                    </span>
                                    <span className="text-base leading-relaxed text-[#2C2B29]">
                                        {label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

/* ============================================================ */
/* Lead Capture                                                 */
/* ============================================================ */
export function LeadCapture({ onSubmit, submitting, subtitle, heading, secondaryHeading, buttonLabel }) {
    const [form, setForm] = useState({
        first_name: "",
        email: "",
        country_code: "+91",
        phone: "",
        consent: false,
    });
    const set = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e?.target?.value ?? e }));

    const submit = (e) => {
        e.preventDefault();
        if (!form.first_name.trim()) return toast.error("Please share your first name.");
        if (!form.email.trim()) return toast.error("Please share your email.");
        if (!/^\d{6,15}$/.test(form.phone.replace(/\D/g, "")))
            return toast.error("Please enter a valid phone number (digits only).");
        if (!form.consent) return toast.error("Please agree to share your details.");
        onSubmit(form);
    };

    return (
        <form onSubmit={submit} data-testid="bp-lead-form">
            <p className="ssh-eyebrow">Almost there</p>
            <h2 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-[#2C2B29]">
                {heading || "Your Relationship Blueprint"}{" "}
                <em className="italic text-[#A68A6B]">
                    {secondaryHeading || "is ready."}
                </em>
            </h2>
            <p className="mt-4 text-base lg:text-lg text-[#4A4947]">
                {subtitle || "Enter your details below to see your personalised results."}
            </p>

            <div className="mt-10 space-y-6 max-w-xl">
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">First Name</Label>
                    <Input
                        value={form.first_name}
                        onChange={set("first_name")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="bp-lead-firstname"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">Email</Label>
                    <Input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="bp-lead-email"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">WhatsApp / Phone</Label>
                    <div className="grid grid-cols-[140px_1fr] gap-3">
                        <Select
                            value={form.country_code}
                            onValueChange={(v) => setForm((s) => ({ ...s, country_code: v }))}
                        >
                            <SelectTrigger
                                className="bg-white border-[#EFEBE4] rounded-none h-12 focus:ring-[#8A9A86]"
                                data-testid="bp-lead-country"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-72">
                                {COUNTRY_CODES.map((c) => (
                                    <SelectItem key={c.code} value={c.code}>
                                        {c.code} · {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            value={form.phone}
                            onChange={set("phone")}
                            placeholder="98765 43210"
                            className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                            data-testid="bp-lead-phone"
                        />
                    </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer text-sm leading-relaxed text-[#4A4947]">
                    <Checkbox
                        checked={form.consent}
                        onCheckedChange={(v) =>
                            setForm((s) => ({ ...s, consent: !!v }))
                        }
                        className="rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B] data-[state=checked]:text-white mt-1"
                        data-testid="bp-lead-consent"
                    />
                    <span>
                        I consent to Soul Sync Healing storing my responses and
                        contact details to send me my Blueprint and any relevant
                        follow-up. I understand this is not a clinical
                        assessment and I can request deletion of my data at any
                        time.
                    </span>
                </label>
                <Button
                    type="submit"
                    disabled={submitting}
                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3"
                    data-testid="bp-lead-submit"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Calculating…
                        </>
                    ) : (
                        <>
                            <ShieldCheck className="h-4 w-4" />
                            {buttonLabel || "Show My Relationship Blueprint"}
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
}

/* ============================================================ */
/* Result                                                       */
/* ============================================================ */
export function ScoreRow({ label, pct, valueLabel, color }) {
    const hex = COLOR_HEX[color] || "#A68A6B";
    return (
        <div className="border-b border-[#EFEBE4] py-5">
            <div className="flex items-baseline justify-between gap-4">
                <div>
                    <p className="ssh-eyebrow">{label}</p>
                    <p
                        className="mt-2 text-sm uppercase tracking-[0.18em] font-medium"
                        style={{ color: hex }}
                    >
                        {valueLabel}
                    </p>
                </div>
                <p className="font-serif text-4xl text-[#2C2B29]">{pct}%</p>
            </div>
            <div className="mt-4 h-2 w-full bg-[#EFEBE4]">
                <motion.div
                    className="h-full"
                    style={{ backgroundColor: hex }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                />
            </div>
        </div>
    );
}

function ResultView({ result }) {
    const isHot = result.lead_temperature === "HOT";
    const isWarm = result.lead_temperature === "WARM";
    return (
        <section className="pt-24 pb-24" data-testid="bp-result">
            <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="ssh-eyebrow"
                >
                    Your Relationship Blueprint
                </motion.p>
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 font-serif text-2xl text-[#4A4947]"
                >
                    {result.first_name}, your current relationship stage is
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-4 font-serif font-light tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
                    style={{ color: "#A68A6B" }}
                >
                    <em className="italic">{result.final_stage}</em>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 bg-white border border-[#EFEBE4] p-8 lg:p-10"
                >
                    <p className="font-serif italic text-xl lg:text-2xl text-[#2C2B29] leading-snug border-l-2 border-[#A68A6B] pl-4">
                        {result.stage_headline}
                    </p>
                    <div className="mt-8 space-y-5">
                        {result.stage_body.map((p, i) => (
                            <p
                                key={i}
                                className="text-base lg:text-lg leading-[1.85] text-[#2C2B29] font-light"
                            >
                                {p}
                            </p>
                        ))}
                    </div>
                    <div className="mt-8 pt-8 border-t border-[#EFEBE4]">
                        <p className="ssh-eyebrow">Your next reflection</p>
                        <p className="mt-3 font-serif text-xl lg:text-2xl italic text-[#2C2B29] leading-snug">
                            {result.stage_reflection}
                        </p>
                    </div>
                </motion.div>

                {/* Scores */}
                <div className="mt-16">
                    <p className="ssh-eyebrow">Your four category scores</p>
                    <div className="mt-6">
                        <ScoreRow
                            label="Disconnection"
                            pct={result.disconnection_percentage}
                            valueLabel={result.disconnection_label}
                            color={result.disconnection_color}
                        />
                        <ScoreRow
                            label="Relationship Distress"
                            pct={result.distress_percentage}
                            valueLabel={result.distress_label}
                            color={result.distress_color}
                        />
                        <ScoreRow
                            label="Stuckness"
                            pct={result.stuckness_percentage}
                            valueLabel={result.stuckness_label}
                            color={result.stuckness_color}
                        />
                        <ScoreRow
                            label="Readiness for Change"
                            pct={result.readiness_percentage}
                            valueLabel={result.readiness_label}
                            color={result.readiness_color}
                        />
                    </div>
                </div>

                {/* Concerns */}
                {result.concerns && result.concerns.length > 0 && (
                    <div className="mt-16">
                        <p className="ssh-eyebrow">
                            What you most want to change
                        </p>
                        <ul className="mt-6 flex flex-wrap gap-2">
                            {result.concerns.map((c) => (
                                <li
                                    key={c}
                                    className="text-xs uppercase tracking-[0.14em] text-[#2C2B29] border border-[#EFEBE4] px-3 py-2 bg-[#FAF8F5]"
                                >
                                    {c}
                                </li>
                            ))}
                        </ul>
                        {result.concern_paragraphs.length > 0 && (
                            <div className="mt-8 space-y-4">
                                {result.concern_paragraphs.map((p, i) => (
                                    <p
                                        key={i}
                                        className="text-base lg:text-lg leading-[1.75] text-[#4A4947] font-light"
                                    >
                                        {p}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10% 0px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className={`mt-20 border p-8 lg:p-12 ${
                        isHot
                            ? "bg-[#2C2B29] border-[#2C2B29] text-white"
                            : "bg-[#FAF8F5] border-[#EFEBE4]"
                    }`}
                    data-testid="bp-cta-section"
                >
                    <p
                        className="ssh-eyebrow"
                        style={{ color: isHot ? "#C5A880" : undefined }}
                    >
                        {isHot ? "You said you're ready" : "A gentle next step"}
                    </p>
                    <h3
                        className={`mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl lg:text-4xl ${
                            isHot ? "text-white" : "text-[#2C2B29]"
                        }`}
                    >
                        Want to understand your Blueprint{" "}
                        <em
                            className="italic"
                            style={{
                                color: isHot ? "#C5A880" : "#A68A6B",
                            }}
                        >
                            more deeply?
                        </em>
                    </h3>
                    <p
                        className={`mt-5 text-base lg:text-lg leading-relaxed max-w-2xl ${
                            isHot ? "text-[#d8d3c8]" : "text-[#4A4947]"
                        }`}
                    >
                        Your scorecard shows where you may currently be. The
                        next step is understanding what may be creating the
                        pattern — and what kind of change would actually feel
                        meaningful for you.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <Link to="/contact#booking">
                            <Button
                                className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                data-testid="bp-cta-book"
                            >
                                Book a Relationship Clarity Call
                            </Button>
                        </Link>
                        {(isWarm || !isHot) && (
                            <Link to="/blog">
                                <Button
                                    variant="outline"
                                    className={`rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors ${
                                        isHot
                                            ? "border-white bg-transparent text-white hover:bg-white hover:text-[#2C2B29]"
                                            : "border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white"
                                    }`}
                                >
                                    Read Insights First
                                </Button>
                            </Link>
                        )}
                    </div>
                </motion.div>

                <div className="mt-12 text-center">
                    <Link
                        to="/blueprint"
                        className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] inline-flex items-center gap-2"
                        data-testid="bp-explore-another"
                    >
                        Explore another Blueprint
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Link>
                </div>

                <p className="mt-10 text-xs text-[#75736F] leading-relaxed max-w-2xl border-l-2 border-[#EFEBE4] pl-4">
                    This is a self-reflection tool for personal insight and is
                    not a psychological diagnosis, clinical assessment,
                    relationship-safety assessment, or substitute for
                    professional mental-health support.{" "}
                    <a
                        href={`https://wa.me/${SITE.whatsappRaw}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-[#A68A6B]"
                    >
                        Reach out to Shreya
                    </a>{" "}
                    if you need to talk with someone.
                </p>
            </div>
        </section>
    );
}

/* ============================================================ */
/* Root                                                         */
/* ============================================================ */
export default function BlueprintPage() {
    useSetTitle("Your Relationship Blueprint · Soul Sync Healing");
    const [step, setStep] = useState("intro"); // intro | q | lead | result
    const [qIdx, setQIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const totalSteps = QUESTIONS.length;
    const current = QUESTIONS[qIdx];
    const currentValue = current
        ? current.multi
            ? answers[`q${current.id}`] || []
            : answers[`q${current.id}`]
        : undefined;

    const canProceed = useMemo(() => {
        if (!current) return false;
        if (current.multi) {
            return Array.isArray(currentValue) && currentValue.length > 0;
        }
        return currentValue !== undefined && currentValue !== null && currentValue !== "";
    }, [current, currentValue]);

    // Scroll to top on step change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step, qIdx]);

    const setAnswer = (val) => {
        setAnswers((s) => ({ ...s, [`q${current.id}`]: val }));
    };

    const next = () => {
        if (!canProceed) return;
        if (qIdx < totalSteps - 1) {
            setQIdx((i) => i + 1);
        } else {
            setStep("lead");
        }
    };
    const back = () => {
        if (qIdx > 0) setQIdx((i) => i - 1);
        else setStep("intro");
    };

    const submit = async (lead) => {
        setSubmitting(true);
        try {
            // Build payload: convert Q4 "na" to null, keep multi/string
            const a = {};
            for (let i = 1; i <= 18; i++) {
                const v = answers[`q${i}`];
                if (v === "na") a[`q${i}`] = null;
                else a[`q${i}`] = typeof v === "number" ? v : null;
            }
            a.q19 = answers.q19 || [];
            a.q20 = answers.q20 || null;
            const { data } = await axios.post(`${API}/blueprint/submit`, {
                ...lead,
                answers: a,
            });
            setResult(data);
            setStep("result");
        } catch (err) {
            toast.error(
                "Something went wrong. Please try again in a moment.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (step === "result" && result) return <ResultView result={result} />;
    if (step === "intro") return <Intro onStart={() => setStep("q")} />;

    return (
        <section className="pt-24 pb-24" data-testid="blueprint-assessment">
            <div className="max-w-3xl mx-auto px-6 lg:px-12 pt-10">
                {step === "q" && current && (
                    <>
                        <QuestionCard
                            question={current}
                            value={currentValue}
                            onChange={setAnswer}
                            index={qIdx}
                            total={totalSteps}
                        />
                        <div className="mt-12 flex items-center justify-between gap-4">
                            <Button
                                onClick={back}
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-7 py-6 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 transition-colors"
                                data-testid="bp-back"
                            >
                                <ArrowLeft
                                    className="h-4 w-4"
                                    strokeWidth={1.5}
                                />
                                Back
                            </Button>
                            <Button
                                onClick={next}
                                disabled={!canProceed}
                                className="ssh-btn-primary rounded-none px-7 py-6 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 disabled:opacity-40"
                                data-testid="bp-next"
                            >
                                {qIdx < totalSteps - 1 ? "Next" : "Continue"}
                                <ArrowRight
                                    className="h-4 w-4"
                                    strokeWidth={1.5}
                                />
                            </Button>
                        </div>
                    </>
                )}
                {step === "lead" && (
                    <>
                        <LeadCapture
                            onSubmit={submit}
                            submitting={submitting}
                        />
                        <div className="mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setStep("q");
                                    setQIdx(totalSteps - 1);
                                }}
                                className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] inline-flex items-center gap-2"
                                data-testid="bp-lead-back"
                            >
                                <ArrowLeft
                                    className="h-3.5 w-3.5"
                                    strokeWidth={1.5}
                                />
                                Back to questions
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
